import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Reveal } from "./Reveal";

const STAGES = [
  {
    window: "First few hours",
    severity: 30,
    color: "#7FD1C4",
    body: "Water spreads and wicks into drywall, baseboards, subfloor, and framing. What was surface water is now inside the structure. Absorbent materials — carpet, insulation, drywall — begin to saturate.",
  },
  {
    window: "24–48 hours",
    severity: 55,
    color: "#3D8DB0",
    body: "Mold can begin to grow. Drywall swells and weakens. This is the window that separates a dry-out from a tear-out.",
  },
  {
    window: "48–72 hours +",
    severity: 80,
    color: "#12496B",
    body: "Mold colonies establish and spread through wall cavities and under flooring — places you can't see. The category of the water can worsen as it sits. Structural materials may pass the point of drying and have to be removed.",
  },
  {
    window: "First week and beyond",
    severity: 100,
    color: "#141B22",
    body: "What could have been extraction and drying becomes demolition, material replacement, and remediation — a longer, costlier, more invasive job, and a health consideration for anyone living in the home.",
  },
];

const MOLD = [
  "Mold needs only moisture, an organic surface, and time — an untreated water loss provides all three.",
  "Once it's established in wall cavities and under flooring, it's no longer a cleaning job — it's remediation under a different standard (IICRC S520), which is more involved and more expensive than drying a structure out before it takes hold.",
  "It's not only a building problem. Mold spores affect indoor air quality and can be a health concern for the people living there — especially children, older adults, and anyone with respiratory sensitivity.",
];

const StageRow = ({ stage, reduce }) => (
  <div className="relative pl-8 sm:pl-10 pb-10 last:pb-0">
    <span
      className="absolute left-0 top-1.5 h-3 w-3 rounded-full ring-4 ring-erg-mid/30"
      style={{ backgroundColor: stage.color }}
    />
    <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-erg-dry/55">
      {stage.window}
    </span>
    <div className="mt-2 h-1.5 w-full max-w-md rounded-full bg-white/10 overflow-hidden">
      <motion.div
        className="h-full rounded-full"
        style={{ backgroundColor: stage.color }}
        initial={reduce ? undefined : { scaleX: 0 }}
        whileInView={reduce ? undefined : { scaleX: stage.severity / 100 }}
        viewport={{ once: true, margin: "-40% 0px -40% 0px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      />
    </div>
    <p className="mt-3 text-erg-dry/80 text-base sm:text-lg leading-relaxed max-w-2xl">
      {stage.body}
    </p>
  </div>
);

export const ClockTimeline = () => {
  const reduce = useReducedMotion();
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start 75%", "end 60%"],
  });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section data-testid="clock-timeline-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <span className="font-mono text-[12px] tracking-[0.22em] uppercase text-erg-aqua">
              Why speed matters
            </span>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="mt-4 font-display font-bold uppercase text-erg-dry leading-[0.95] text-[clamp(2rem,6vw,3.75rem)]">
              The clock is the enemy
            </h2>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-5 text-erg-dry/75 text-lg leading-relaxed text-pretty">
              Water damage is a timeline, not a single event. These are general facts about how
              an untreated water loss progresses — not a prediction about any one home. The point
              is simple: what happens next depends on how quickly the water comes out.
            </p>
          </Reveal>
        </div>

        <div ref={listRef} className="mt-14 relative ml-1 sm:ml-2">
          <div className="absolute left-[5px] top-0 bottom-0 w-px bg-erg-aqua/15" aria-hidden="true" />
          <motion.div
            aria-hidden="true"
            className="absolute left-[5px] top-0 bottom-0 w-px origin-top bg-gradient-to-b from-erg-aqua via-erg-shallow to-erg-ink"
            style={{ scaleY: reduce ? 1 : lineScale }}
          />
          <div className="relative">
            {STAGES.map((s) => (
              <StageRow key={s.window} stage={s} reduce={reduce} />
            ))}
          </div>
        </div>

        {/* What mold actually does */}
        <Reveal delay={0.05} className="mt-8">
          <div className="rounded-2xl border border-white/15 bg-erg-deep/25 backdrop-blur-sm p-7 sm:p-10">
            <h3 className="font-display font-semibold uppercase tracking-wide text-erg-dry text-2xl sm:text-3xl">
              What mold actually does
            </h3>
            <ul className="mt-6 space-y-4">
              {MOLD.map((m, i) => (
                <li key={i} className="flex gap-4">
                  <span aria-hidden="true" className="mt-2.5 h-px w-6 shrink-0 bg-erg-aqua sm:w-8" />
                  <p className="text-erg-dry/80 text-base sm:text-lg leading-relaxed text-pretty">{m}</p>
                </li>
              ))}
            </ul>
            <p className="mt-7 border-t border-white/10 pt-6 text-erg-dry text-lg sm:text-xl leading-relaxed font-medium text-pretty">
              The entire point of acting in the first 24–48 hours is to stay ahead of this — to keep
              a water loss a <span className="text-erg-aqua">drying job</span> instead of a
              remediation job.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
